import { useCallback, useEffect, useState } from 'react';
import type { AppConfig } from '@shared/types';
import { ADMIN_USER_ROLE_TIER } from '@shared/types';
import { useAuthStore } from '../../stores/authStore';
import { getAppConfig, updateAppConfig } from '../../services/config';
import type { AppConfigPatch } from '../../services/config';
import { ApiError } from '../../services/api';
import ConfigForm from './ConfigForm';

export default function ConfigPage() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const adminUser = useAuthStore((s) => s.adminUser);

  const readOnly =
    !adminUser ||
    ADMIN_USER_ROLE_TIER[adminUser.role] < ADMIN_USER_ROLE_TIER.admin;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const next = await getAppConfig();
      setConfig(next);
    } catch (err) {
      setLoadError(
        err instanceof ApiError
          ? err.message
          : 'Could not load remote config.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = useCallback(async (patch: AppConfigPatch) => {
    setSaving(true);
    try {
      const next = await updateAppConfig(patch);
      setConfig(next);
    } finally {
      setSaving(false);
    }
  }, []);

  return (
    <div className="p-6 max-w-3xl" data-testid="config-page">
      <header className="mb-6 space-y-1">
        <h1 className="text-xl font-semibold text-slate-900">Remote Config</h1>
        <p className="text-sm text-slate-600">
          Live-editable configuration for the mobile app. Changes fan out to
          every connected client within a few seconds.
        </p>
      </header>

      {loading && (
        <p className="text-sm text-slate-500" data-testid="config-page-loading">
          Loading…
        </p>
      )}

      {loadError && (
        <p
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
          data-testid="config-page-load-error"
        >
          {loadError}
        </p>
      )}

      {config && (
        <>
          <div
            className="mb-4 text-xs text-slate-500 font-mono"
            data-testid="config-page-meta"
          >
            v{config.version} · updated {new Date(config.updatedAt).toLocaleString()}
          </div>
          <ConfigForm
            config={config}
            readOnly={readOnly}
            busy={saving}
            onSave={handleSave}
          />
        </>
      )}
    </div>
  );
}
